import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { MailCheck } from 'lucide-react';

import { Brand, Callout, ErrorCallout, Spinner } from '../components/ui';
import { track } from '../lib/analytics';
import { api, ApiError, asApiError } from '../lib/api';
import { useDocumentTitle } from '../lib/hooks';
import { forgetLinkToken, linkToken } from '../lib/link-token';
import { useSession } from '../lib/session';
import type { User } from '../lib/types';

/* The page the emailed link opens: /verify#token=…. The token rides in the
   fragment, so it never reaches a server log or a Referer; this page reads it
   (see lib/link-token) and spends it with POST /v1/auth/verify. Signed in or
   not, it works. */

type State = { kind: 'verifying' } | { kind: 'done'; user: User } | { kind: 'failed'; error: ApiError };

export function VerifyPage() {
  useDocumentTitle('Verify your email');
  const { me, refreshMe } = useSession();
  const [state, setState] = useState<State>({ kind: 'verifying' });
  const started = useRef(false);

  useEffect(() => {
    /* Once: React's development double-mount must not spend a single-use token twice. */
    if (started.current) return;
    started.current = true;
    const token = linkToken();
    forgetLinkToken();
    if (token === null) {
      setState({ kind: 'failed', error: new ApiError(400, 'bad-token', 'This page needs the link from your email.', 'Open the link exactly as it arrived.') });
      return;
    }
    api<{ user: User }>('/v1/auth/verify', { method: 'POST', body: { token } }).then(
      async (answer) => {
        setState({ kind: 'done', user: answer.user });
        track('email_verified');
        await refreshMe();
      },
      (error: unknown) => setState({ kind: 'failed', error: asApiError(error) }),
    );
  }, [refreshMe]);

  return (
    <div className="auth">
      <main className="auth__form">
        <div className="verify">
          <Brand to="/" />
          {state.kind === 'verifying' ? (
            <p className="lede" role="status">
              <Spinner /> Verifying your email…
            </p>
          ) : state.kind === 'done' ? (
            <Callout tone="ok" role="status" title={`${state.user.email} is verified.`}>
              Your team can now make API keys and run sandboxes and tasks.{' '}
              {me ? <Link to="/">Go to the console</Link> : <Link to="/sign-in">Sign in to start</Link>}.
            </Callout>
          ) : state.error.code === 'bad-token' ? (
            me?.user.emailVerified ? (
              <Callout tone="ok" title={`${me.user.email} is already verified.`}>
                <Link to="/">Go to the console</Link>.
              </Callout>
            ) : (
              <Callout tone="error" role="alert" title="That link doesn't work anymore.">
                Links work once and expire after 24 hours. {me ? 'Send a new one from the banner at the top of the console.' : 'Sign in, and send a new one from the console.'}{' '}
                <Link to={me ? '/' : '/sign-in'}>{me ? 'Go to the console' : 'Sign in'}</Link>.
              </Callout>
            )
          ) : (
            <ErrorCallout error={state.error} />
          )}
        </div>
      </main>
    </div>
  );
}

/** Across the top of every console page while the team cannot run work yet. */
export function VerifyBanner() {
  const { me } = useSession();
  const [sent, setSent] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  if (!me || me.team.verified) return null;

  async function resend() {
    setBusy(true);
    setError(null);
    try {
      const answer = await api<{ sentTo: string }>('/v1/auth/resend-verification', { method: 'POST' });
      setSent(answer.sentTo);
    } catch (err) {
      setError(asApiError(err));
    } finally {
      setBusy(false);
    }
  }

  if (!me.user.emailVerified) {
    return (
      <div className="banner" role="note">
        <MailCheck width={15} height={15} aria-hidden="true" />
        <strong>Check your email</strong>
        <span>
          {sent ? `A new link is on its way to ${sent}.` : `We sent a link to ${me.user.email}.`} Open it to make keys and run sandboxes and tasks; until then you can look around.
        </span>
        <button type="button" className="btn btn--sm" onClick={() => void resend()} disabled={busy} aria-busy={busy}>
          {busy ? <Spinner /> : null}
          Send it again
        </button>
        {error ? (
          <span role="alert">
            {error.message}
            {error.fix ? ` ${error.fix}` : ''}
          </span>
        ) : null}
      </div>
    );
  }
  return (
    <div className="banner" role="note">
      <MailCheck width={15} height={15} aria-hidden="true" />
      <strong>Waiting on the owner</strong>
      <span>This team can run work once its owner verifies their email. Switch to another team, or ask them to open their link.</span>
    </div>
  );
}
